import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, CircularProgress, Alert, Snackbar, TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getObraDocumentos, uploadObraDocumento, deleteObraDocumento, downloadObraDocumento, getObraDocumentoUrl, listUsers, ObraDocumento } from '../services/api';

interface ObraDocumentacaoTabProps {
    obraId: number;
}

const ObraDocumentacaoTab: React.FC<ObraDocumentacaoTabProps> = ({ obraId }) => {
    const [documentos, setDocumentos] = useState<ObraDocumento[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [customName, setCustomName] = useState('');
    const [userMap, setUserMap] = useState<{ [key: string]: string }>({});

    const fetchUsers = useCallback(async () => {
        try {
            const users = await listUsers();
            const map: { [key: string]: string } = {};
            users.forEach((u: any) => {
                map[u.id] = u.user_metadata?.name || u.email;
            });
            setUserMap(map);
        } catch (err) {
            console.error('Erro ao carregar usuários:', err);
        }
    }, []);

    const fetchDocumentos = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getObraDocumentos(obraId);
            setDocumentos(data);
        } catch (err) {
            console.error('Erro ao carregar documentos:', err);
            setError('Erro ao carregar documentos.');
        } finally {
            setLoading(false);
        }
    }, [obraId]);

    useEffect(() => {
        fetchDocumentos();
        fetchUsers();
    }, [fetchDocumentos, fetchUsers]);

    const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) return;

        const file = event.target.files[0];
        setUploading(true);
        setError(null);

        try {
            await uploadObraDocumento(obraId, file, customName);
            setSuccessMessage('Documento enviado com sucesso!');
            setCustomName(''); // Clear custom name after upload
            fetchDocumentos();
        } catch (err) {
            console.error('Erro ao enviar documento:', err);
            setError('Erro ao enviar documento.');
        } finally {
            setUploading(false);
            // Reset input value to allow uploading the same file again if needed
            event.target.value = '';
        }
    };

    const handleDelete = async (id: string, caminho: string) => {
        if (!window.confirm('Tem certeza que deseja excluir este documento?')) return;

        try {
            await deleteObraDocumento(id, caminho);
            setSuccessMessage('Documento excluído com sucesso!');
            setDocumentos(documentos.filter(doc => doc.id !== id));
        } catch (err) {
            console.error('Erro ao excluir documento:', err);
            setError('Erro ao excluir documento.');
        }
    };

    const handleDownload = async (caminho: string, nome: string) => {
        try {
            const blob = await downloadObraDocumento(caminho);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nome);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
        } catch (err) {
            console.error('Erro ao baixar documento:', err);
            setError('Erro ao baixar documento.');
        }
    };

    const handlePreview = async (caminho: string) => {
        try {
            const url = await getObraDocumentoUrl(caminho);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Erro ao visualizar documento:', err);
            setError('Erro ao visualizar documento.');
        }
    };

    const formatBytes = (bytes: number, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" component="h2">
                    Documentos da Obra
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        label="Nome do Documento (Opcional)"
                        variant="outlined"
                        size="small"
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        sx={{ minWidth: 250 }}
                    />
                    <Button
                        component="label"
                        variant="contained"
                        startIcon={uploading ? <CircularProgress size={24} color="inherit" /> : <CloudUploadIcon />}
                        disabled={uploading}
                    >
                        Upload de Arquivo
                        <input
                            type="file"
                            hidden
                            onChange={handleUpload}
                        />
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : documentos.length === 0 ? (
                <Typography variant="body1" color="textSecondary" align="center">
                    Nenhum documento encontrado.
                </Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Responsável</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Tamanho</TableCell>
                                <TableCell>Data de Envio</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {documentos.map((doc) => (
                                <TableRow key={doc.id}>
                                    <TableCell>{doc.nome}</TableCell>
                                    <TableCell>{userMap[doc.usuario_id] || 'Desconhecido'}</TableCell>
                                    <TableCell>{doc.tipo}</TableCell>
                                    <TableCell>{formatBytes(doc.tamanho)}</TableCell>
                                    <TableCell>{new Date(doc.criado_em).toLocaleDateString('pt-BR')}</TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => handlePreview(doc.caminho)} title="Visualizar">
                                            <VisibilityIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleDownload(doc.caminho, doc.nome)} title="Baixar">
                                            <DownloadIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleDelete(doc.id, doc.caminho)} color="error" title="Excluir">
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Snackbar
                open={!!successMessage}
                autoHideDuration={6000}
                onClose={() => setSuccessMessage(null)}
                message={successMessage}
            />
        </Box>
    );
};

export default ObraDocumentacaoTab;
